import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthSession } from "../contexts/AuthSessionContext";
import { oktaConfig } from "../config/okta";
import { verifyOktaWithBackend } from "../services/authService";
import { clearAuthSession } from "../services/authSessionService";

type CallbackState = "loading" | "success" | "error";

/**
 * Authorization codes are single-use. React 18 Strict Mode runs effects twice in dev,
 * which would call /v1/token twice and yield invalid_grant on the second call.
 * Share one in-flight promise per code so the exchange runs only once.
 */
const tokenExchangeByCode = new Map<string, Promise<void>>();

function runTokenExchangeOnce(code: string, run: () => Promise<void>): Promise<void> {
  const existing = tokenExchangeByCode.get(code);
  if (existing) {
    return existing;
  }
  const promise = run().finally(() => {
    tokenExchangeByCode.delete(code);
  });
  tokenExchangeByCode.set(code, promise);
  return promise;
}

export const OktaCallbackPage = () => {
  const navigate = useNavigate();
  const { setSessionFromVerify } = useAuthSession();
  const [status, setStatus] = useState<CallbackState>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const exchange = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const error = params.get("error");
      const errorDesc = params.get("error_description");

      if (error) {
        clearAuthSession();
        setStatus("error");
        setErrorMessage(`Okta error: ${error}${errorDesc ? ` — ${errorDesc}` : ""}`);
        return;
      }

      if (!code) {
        clearAuthSession();
        setStatus("error");
        setErrorMessage("No authorization code received from Okta.");
        return;
      }

      const expectedState = sessionStorage.getItem("pkce_state");
      if (state !== expectedState) {
        clearAuthSession();
        setStatus("error");
        setErrorMessage("State parameter mismatch. Please try logging in again.");
        return;
      }

      const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
      if (!codeVerifier) {
        clearAuthSession();
        setStatus("error");
        setErrorMessage("Missing PKCE verifier. Please try logging in again.");
        return;
      }

      const { issuer, clientId, redirectUri } = oktaConfig;

      try {
        await runTokenExchangeOnce(code, async () => {
          const body = new URLSearchParams({
            grant_type: "authorization_code",
            code,
            redirect_uri: redirectUri,
            client_id: clientId,
            code_verifier: codeVerifier,
          });

          const res = await fetch(`${issuer}/v1/token`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
          });

          if (!res.ok) {
            const errBody = await res.text();
            throw new Error(`Token exchange failed (${res.status}): ${errBody}`);
          }

          const tokens = await res.json();

          const accessToken = String(tokens.access_token ?? "");
          const idToken = String(tokens.id_token ?? "");
          if (!accessToken && !idToken) {
            throw new Error("Token response had no access_token or id_token.");
          }

          const expiresAt =
            tokens.expires_in != null ? Date.now() + Number(tokens.expires_in) * 1000 : null;

          sessionStorage.removeItem("pkce_code_verifier");
          sessionStorage.removeItem("pkce_state");

          const verified = await verifyOktaWithBackend(accessToken, idToken);
          setSessionFromVerify(verified, {
            accessToken,
            idToken,
            expiresAt,
          });
        });

        if (!cancelled) {
          setStatus("success");
          setTimeout(() => navigate("/my-contracts", { replace: true }), 400);
        }
      } catch (err) {
        clearAuthSession();
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(err instanceof Error ? err.message : "Token exchange failed.");
        }
      }
    };

    void exchange();
    return () => { cancelled = true; };
  }, [navigate, setSessionFromVerify]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-light-bg font-sans">
      <div className="w-full max-w-md px-6">
        <div className="rounded-2xl border border-gray-200 bg-white px-8 py-10 shadow-card">
          <div className="mb-6 flex justify-center">
            <img
              src="/assets/tokio-marine-logo.png"
              alt="Tokio Marine HCC"
              className="h-10 w-auto"
            />
          </div>

          {status === "loading" && (
            <div className="flex flex-col items-center gap-3">
              <svg className="h-8 w-8 animate-spin text-primary-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm font-medium text-heading">Completing sign-in…</p>
              <p className="text-xs text-body/50">Exchanging authorization code for tokens.</p>
            </div>
          )}

          {status === "success" && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-medium text-heading">Sign-in successful</p>
              <p className="text-xs text-body/50">Redirecting to the application…</p>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-heading">Sign-in failed</p>
              <p className="max-w-xs text-center text-xs text-body/60 leading-relaxed">
                {errorMessage}
              </p>
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="mt-2 rounded-lg bg-primary-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
